<script lang="ts">
  import { commonName, isTestsGroup, itemIcon, receiptIsOpen, receiptLockPatch, type CatalogNote, type TripSummary } from "@split/core";
  import * as R from "../settings/receipts.ts";
  import * as H from "../settings/hints.ts";
  import { fill, shows } from "../settings/index.ts";
  import { app } from "../lib/store.svelte.ts";
  const groupName = $derived(app.groups.find((g) => g.id === app.groupId)?.name ?? app.current.name);
  import { catalogLookup } from "../lib/ai/lookup.svelte.ts";
  import { aiQueue } from "../lib/ai/queue.svelte.ts";
  import { link } from "../lib/router.svelte.ts";
  import ReceiptScan from "../components/ReceiptScan.svelte";
  import ReceiptTests from "../components/ReceiptTests.svelte";
  let scanner = $state<ReturnType<typeof ReceiptScan> | undefined>();
  import PriceWatch from "../components/PriceWatch.svelte";
  import { centsInput, formatCents, formatMicro, readMoney, receiptName, shortDate } from "../lib/format.ts";

  const L = $derived(app.ledger);
  let catalogQuery = $state("");
  const catalog = $derived.by(() => {
    const q = catalogQuery.trim().toLowerCase();
    const kw = (no: string) => (notes[`#${no}`]?.keywords ?? []).join(" ");
    const rows = q
      ? L.catalog.filter((c) => c.itemNo.includes(q) || c.name.toLowerCase().includes(q) || kw(c.itemNo).includes(q))
      : L.catalog;
    return rows.slice().sort((a, b) => (a.lastDate === b.lastDate ? a.name.localeCompare(b.name) : a.lastDate < b.lastDate ? 1 : -1));
  });
  let drafts = $state<Record<string, string>>({});
  const notes = $derived(app.viewState.catalogNotes ?? {});
  /** Products the AI says aren't usually taxed, but that were charged tax. */
  const taxFlags = $derived.by(() => {
    const out: { itemNo: string; name: string; note: string }[] = [];
    for (const c of L.catalog) {
      const n = notes[`#${c.itemNo}`];
      if (n?.taxable !== false || n.source !== "ai") continue;
      const taxed = L.items.some((i) => i.item.itemNo === c.itemNo && i.taxBp > 0);
      if (taxed) out.push({ itemNo: c.itemNo, name: c.name, note: n.note ?? "food" });
    }
    return out;
  });

  async function saveNote(itemNo: string, patch: Partial<CatalogNote>) {
    const key = `#${itemNo}`;
    const cur = notes[key] ?? { keywords: [] };
    const next: CatalogNote = { ...cur, ...patch, source: "you" };
    const empty = !next.keywords.length && !next.icon && (next.taxable === undefined || next.taxable === null) && !next.note && next.priceNowCents === undefined;
    await app.do_({ type: "catalog.note", key, note: empty ? null : next }, "Saved");
  }
  const words = (text: string) =>
    text
      .split(",")
      .map((w) => w.trim().toLowerCase())
      .filter(Boolean);

  function value(t: TripSummary) {
    return drafts[t.key] ?? (t.receiptCents === null ? "" : centsInput(t.receiptCents));
  }

  async function commit(t: TripSummary) {
    const text = drafts[t.key];
    if (text === undefined) return;
    const cents = text.trim() === "" ? null : readMoney(text);
    if (cents !== null && cents < 0) return;
    if (text.trim() !== "" && cents === null) {
      app.toast("Enter the receipt total like 622.90", "error");
      return;
    }
    await app.do_({ type: "receipt.set", date: t.date, store: t.store, receipt: t.receipt || undefined, receiptId: t.receiptId, cents });
    delete drafts[t.key];
  }

  const isOpen = (t: TripSummary) => receiptIsOpen(app.viewState.settings, t);
  async function setLocked(t: TripSummary, locked: boolean) {
    const name = `${receiptName(t.store, t.receipt)}, ${shortDate(t.date)}`;
    await app.do_(
      { type: "settings.update", patch: receiptLockPatch(app.viewState.settings, t, locked) },
      fill(locked ? R.RECEIPT_LOCKED_DONE : R.RECEIPT_OPENED_DONE, { name }),
    );
  }

  function status(t: TripSummary): { text: string; bad: boolean } {
    if (t.diffMicro === null) return { text: "Add the printed total to check it", bad: false };
    if (Math.abs(t.diffMicro) <= 5000) return { text: "Matches the receipt", bad: false };
    return { text: `Rows are ${formatMicro(Math.abs(t.diffMicro))} ${t.diffMicro > 0 ? "over" : "under"}`, bad: true };
  }
</script>

<div class="page">
  <div class="page-head">
    <div>
      <h1>Receipts and catalog</h1>
      {#if H.RECEIPTS_LEDE.trim()}<p class="lede">{H.RECEIPTS_LEDE}</p>{/if}
    </div>
  </div>

  {#if app.role === "owner" && app.canEdit}
    <ReceiptScan bind:this={scanner} />
    {#if isTestsGroup(groupName)}<ReceiptTests onreview={(drafts, photo) => scanner?.queue(drafts, photo)} />{/if}
  {/if}

  <div class="list">
    {#each L.trips as t (t.key)}
      {@const s = status(t)}
      <div class="trip">
        <div class="what">
          <strong>{receiptName(t.store, t.receipt)}</strong>
          <span class="muted">{shortDate(t.date)}, {t.itemCount} {t.itemCount === 1 ? "row" : "rows"} adding up to {formatMicro(t.totalMicro)}{t.cards.length ? `, on ${t.cards.join(" and ")}` : ""}</span>
          <span class="status" class:bad={s.bad}>{s.text}</span>
        </div>
        <div class="lock">
          {#if shows(isOpen(t) ? R.RECEIPT_OPEN_TEXT : R.RECEIPT_LOCKED_TEXT)}
            <span class="chip" class:locked={!isOpen(t)} data-testid="receipt-lock-state">{isOpen(t) ? R.RECEIPT_OPEN_TEXT : R.RECEIPT_LOCKED_TEXT}</span>
          {/if}
          {#if app.canEdit}
            <button class="btn small" onclick={() => setLocked(t, isOpen(t))}>{isOpen(t) ? R.RECEIPT_LOCK_BUTTON : R.RECEIPT_OPEN_BUTTON}</button>
          {/if}
        </div>
        <label class="field">
          <span>Receipt total</span>
          <input
            class="input money"
            inputmode="decimal"
            placeholder="0.00"
            value={value(t)}
            disabled={!app.canEdit}
            oninput={(e) => (drafts[t.key] = (e.currentTarget as HTMLInputElement).value)}
            onblur={() => commit(t)}
            onkeydown={(e) => e.key === "Enter" && (e.currentTarget as HTMLInputElement).blur()}
          />
        </label>
      </div>
    {:else}
      <div class="empty">Trips appear here once you add items.</div>
    {/each}
  </div>

  <PriceWatch />

  <section class="catalog">
    <div class="cat-head">
      <h2>Item catalog</h2>
      <input class="input" type="search" placeholder="Item number or name" bind:value={catalogQuery} aria-label="Search the catalog" />
    </div>
    {#if H.CATALOG_NOTE.trim()}<p class="muted note">{H.CATALOG_NOTE}</p>{/if}
    {#if app.role === "owner" && app.canEdit}
      <div class="lookup">
        <div>
          <strong>Look up with AI</strong>
          {#if H.CATALOG_AI_NOTE.trim()}<p class="muted small">{H.CATALOG_AI_NOTE}</p>{/if}
          {#if catalogLookup.running}
            <p class="small">Looking up {catalogLookup.done} of {catalogLookup.total}…</p>
          {:else if catalogLookup.error}
            <p class="small owes">{catalogLookup.error}</p>
          {:else}
            <p class="small muted">{catalogLookup.pending().length} {catalogLookup.pending().length === 1 ? "product has" : "products have"} no notes yet.</p>
          {/if}
        </div>
        <div class="lookup-actions">
          {#if catalogLookup.running}
            <button class="btn small" onclick={() => catalogLookup.stop()}>Stop</button>
          {:else}
            <button class="btn small primary" disabled={!catalogLookup.pending().length || !app.canEdit} onclick={() => aiQueue.first(() => catalogLookup.start(), "Products", () => (catalogLookup.total ? [catalogLookup.done, catalogLookup.total] : null))}>Look up {catalogLookup.pending().length || ""}</button>
            <button class="btn small quiet" disabled={!catalogLookup.pending(true).length || !app.canEdit} onclick={() => aiQueue.first(() => catalogLookup.start(true), "Products", () => (catalogLookup.total ? [catalogLookup.done, catalogLookup.total] : null))} title="Ask again for products the AI already looked up">Redo AI notes</button>
          {/if}
          <label class="auto"><input type="checkbox" checked={catalogLookup.auto} onchange={(e) => catalogLookup.setAuto((e.currentTarget as HTMLInputElement).checked)} /> Look up new products by itself (when you're idle)</label>
        </div>
      </div>
    {/if}
    {#if taxFlags.length}
      <div class="taxflags">
        <strong>Tax check</strong>
        <p class="muted small">The AI thinks these usually aren't taxed in California, but tax was charged on them. Worth a look on the receipt.</p>
        <ul>
          {#each taxFlags as f (f.itemNo)}
            <li><span class="code">#{f.itemNo}</span> {f.name} <span class="muted">({f.note})</span></li>
          {/each}
        </ul>
      </div>
    {/if}
    <div class="list">
      <div class="crow head" aria-hidden="true"><span>Item</span><span>Last price</span><span>Last bought</span><span>Times</span></div>
      {#each catalog as c (c.itemNo)}
        {@const note = notes[`#${c.itemNo}`]}
        <div class="crow">
          <span class="what">
            <span><span class="icon" aria-hidden="true">{itemIcon(c.name, "", note)}</span><span class="code">#{c.itemNo}</span> {c.name}{#if commonName(c.name, note)}{" "}<span class="muted">({commonName(c.name, note)})</span>{/if}</span>
            <span class="edit">
              <input
                class="input kw"
                value={(note?.keywords ?? []).join(", ")}
                placeholder="Other names, like arizona, iced tea"
                aria-label="Keywords for {c.name}"
                disabled={!app.canEdit}
                onchange={(e) => saveNote(c.itemNo, { keywords: words((e.currentTarget as HTMLInputElement).value) })}
              />
              <input
                class="input ic"
                value={note?.icon ?? ""}
                placeholder="Icon"
                maxlength="4"
                aria-label="Icon for {c.name}"
                disabled={!app.canEdit}
                onchange={(e) => saveNote(c.itemNo, { icon: (e.currentTarget as HTMLInputElement).value.trim() || undefined })}
              />
              <select
                class="input tx"
                value={note?.taxable === true ? "yes" : note?.taxable === false ? "no" : ""}
                aria-label="Taxed?"
                disabled={!app.canEdit}
                onchange={(e) => {
                  const v = (e.currentTarget as HTMLSelectElement).value;
                  void saveNote(c.itemNo, { taxable: v === "yes" ? true : v === "no" ? false : null });
                }}
              >
                <option value="">Taxed?</option>
                <option value="yes">Taxed</option>
                <option value="no">Not taxed</option>
              </select>
              {#if note?.source === "ai"}<span class="ai" title="Filled in by the AI lookup">AI</span>{/if}
              {#if note?.note}<span class="muted small">{note.note}</span>{/if}
            </span>
          </span>
          <span class="num">{formatCents(c.lastPriceCents)}</span>
          <span class="muted">{shortDate(c.lastDate)}</span>
          <span class="num">{c.timesBought}</span>
        </div>
      {:else}
        <div class="empty">{catalogQuery ? "No items match." : "Items with an item number show up here."}</div>
      {/each}
    </div>
  </section>
</div>

<style>
  .lookup,
  .taxflags {
    display: flex;
    gap: 16px;
    justify-content: space-between;
    flex-wrap: wrap;
    margin-bottom: 14px;
    padding: 14px 16px;
    border-radius: var(--r-lg);
    background: color-mix(in srgb, #8b5cd6 8%, var(--surface));
    border: 1px solid color-mix(in srgb, #8b5cd6 25%, transparent);
  }

  .taxflags {
    display: block;
    background: color-mix(in srgb, #d9a514 10%, var(--surface));
    border-color: color-mix(in srgb, #d9a514 35%, transparent);
  }

  .taxflags ul {
    margin: 6px 0 0;
    padding-left: 18px;
    font-size: var(--t-sm);
  }

  .lookup > div:first-child {
    flex: 1 1 320px;
  }

  .lookup p {
    margin: 4px 0 0;
  }

  .lookup-actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: flex-start;
  }

  .auto {
    display: inline-flex;
    gap: 6px;
    align-items: center;
    font-size: var(--t-xs);
  }

  .small {
    font-size: var(--t-xs);
  }

  .what {
    display: grid;
    gap: 6px;
    min-width: 0;
  }

  .icon {
    display: inline-block;
    width: 1.5em;
    text-align: center;
  }

  .edit {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    align-items: center;
  }

  .edit .input {
    padding: 4px 8px;
    min-height: 30px;
    font-size: var(--t-xs);
  }

  .kw {
    flex: 1 1 200px;
  }

  .ic {
    width: 64px;
  }

  .tx {
    width: auto;
  }

  .ai {
    padding: 1px 6px;
    border-radius: 999px;
    background: color-mix(in srgb, #8b5cd6 16%, transparent);
    color: #8b5cd6;
    font-size: 0.6875rem;
    font-weight: 700;
  }

  .catalog {
    margin-top: 32px;
  }

  .cat-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 6px;
  }

  .cat-head .input {
    max-width: 280px;
  }

  .note {
    font-size: var(--t-sm);
    margin-bottom: 10px;
  }

  .crow {
    display: grid;
    grid-template-columns: 1fr 100px 100px 60px;
    gap: 12px;
    align-items: center;
    padding: 9px 16px;
    background: var(--surface);
    font-size: var(--t-sm);
  }

  .crow.head {
    font-size: var(--t-xs);
    font-weight: 600;
    color: var(--muted);
  }

  .crow > span:nth-child(n + 2) {
    text-align: right;
  }

  @media (max-width: 560px) {
    .crow {
      grid-template-columns: 1fr 80px;
    }

    .crow > span:nth-child(3),
    .crow > span:nth-child(4) {
      display: none;
    }
  }

  .trip {
    display: grid;
    grid-template-columns: 1fr auto 150px;
    gap: 16px;
    align-items: center;
    padding: 14px 16px;
    background: var(--surface);
  }

  .what {
    display: grid;
    gap: 2px;
  }

  .what .muted {
    font-size: var(--t-sm);
  }

  .status {
    font-size: var(--t-sm);
    font-weight: 600;
    color: var(--ok);
  }

  .status.bad {
    color: var(--owes);
  }

  .lock {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .lock .chip {
    font-size: var(--t-xs);
    font-weight: 600;
    padding: 2px 8px;
    border-radius: var(--r-sm);
    background: var(--wash);
    color: var(--ok);
  }

  .lock .chip.locked {
    color: var(--muted);
  }

  @media (max-width: 640px) {
    .trip {
      grid-template-columns: 1fr;
    }
  }
</style>
